import React from 'react';
import { DBIMenuType } from '@intelligence/enums/menu';

import type { IDBIMenuVO } from '@intelligence/types/menu';

interface IMenuSearchOption {
  groupKey: string;
  value: string;
  text: React.ReactNode;
  rawText: string;
  menu: IDBIMenuVO;
}

interface IMatchRange {
  start: number;
  end: number;
}

const getMenuTitle = (isCn: boolean, menu: IDBIMenuVO): string => {
  if (isCn) {
    return menu?.titleCN || menu?.title || '';
  }

  return menu?.title || menu?.titleCN || '';
};

export const flattenSearchableMenus = (
  isCn: boolean,
  menus: IDBIMenuVO[] | undefined,
  parentTitles: string[] = [],
): IMenuSearchOption[] => {
  if (!Array.isArray(menus) || menus.length === 0) {
    return [];
  }

  const options: IMenuSearchOption[] = [];

  menus.forEach((menu) => {
    const currentTitle = getMenuTitle(isCn, menu);
    const titleChain = [...parentTitles, currentTitle].filter(Boolean);
    const menuType = menu?.type;
    const hasClickableTarget = menuType && menuType !== DBIMenuType.Directory && !!(menu?.path || menu?.params?.url);

    if (hasClickableTarget) {
      const optionText = titleChain.join(' / ');
      options.push({
        groupKey: 'page',
        value: String(menu?.id || ''),
        text: optionText,
        rawText: optionText,
        menu,
      });
    }

    const childMenus = flattenSearchableMenus(isCn, menu?.subMenus, titleChain);
    if (childMenus.length > 0) {
      options.push(...childMenus);
    }
  });

  return options;
};

const SPACE_REGEXP = /\s/;

export const normalizeKeyword = (keyword: string) => {
  return keyword.trim().replace(/\s+/g, ' ').toLowerCase();
};

const getNormalizedTextInfo = (text: string) => {
  let normalizedText = '';
  const indexMap: number[] = [];
  let lastWhitespaceIndex = -1;

  Array.from(text || '').forEach((char, index) => {
    if (SPACE_REGEXP.test(char)) {
      if (normalizedText && lastWhitespaceIndex === -1) {
        lastWhitespaceIndex = index;
      }
      return;
    }

    if (lastWhitespaceIndex !== -1) {
      normalizedText += ' ';
      indexMap.push(lastWhitespaceIndex);
      lastWhitespaceIndex = -1;
    }

    normalizedText += char.toLowerCase();
    indexMap.push(index);
  });

  return {
    normalizedText,
    indexMap,
  };
};

export const getMatchRanges = (text: string, normalizedKeyword: string): IMatchRange[] => {
  if (!text || !normalizedKeyword) {
    return [];
  }

  const { normalizedText, indexMap } = getNormalizedTextInfo(text);
  const ranges: IMatchRange[] = [];
  let searchStartIndex = 0;

  while (searchStartIndex < normalizedText.length) {
    const matchedIndex = normalizedText.indexOf(normalizedKeyword, searchStartIndex);

    if (matchedIndex === -1) {
      break;
    }

    ranges.push({
      start: indexMap[matchedIndex],
      end: indexMap[matchedIndex + normalizedKeyword.length - 1] + 1,
    });
    searchStartIndex = matchedIndex + normalizedKeyword.length;
  }

  return ranges;
};

export const renderHighlightedText = (text: string, matchRanges: IMatchRange[]) => {
  if (matchRanges.length === 0) {
    return text;
  }

  const nodes: React.ReactNode[] = [];
  let currentIndex = 0;

  matchRanges.forEach((range, index) => {
    if (currentIndex < range.start) {
      nodes.push(text.slice(currentIndex, range.start));
    }

    nodes.push(
      <span className="dbi-menu-search__highlight" key={`${range.start}-${range.end}-${index}`}>
        {text.slice(range.start, range.end)}
      </span>,
    );
    currentIndex = range.end;
  });

  if (currentIndex < text.length) {
    nodes.push(text.slice(currentIndex));
  }

  return nodes;
};
